import React, { useState } from "react";
import { Button, Tooltip, Snackbar, Alert, useMediaQuery, Typography,} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useLanguage } from "../context/LanguageContext.js";
import { logDev } from "../utils/logger.js";

/**
 * Bouton pour copier tous les emails de la période dominante
 * @param {Array} emails - Tableau d'adresses email
 * @param {boolean} disabled - Désactiver le bouton
 */

const BoutonEmailPeriodeDominante = ({ emails = [], disabled }) => {
  // Hook MUI pour détecter si on est sur mobile (écran < 600px)
  const isMobile = useMediaQuery("(max-width:600px)");
  const { t } = useLanguage();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const handleClose = () => setSnackbar({ ...snackbar, open: false });

  // On transforme le tableau en chaîne séparée par des virgules
  const cleanedEmails = Array.isArray(emails)
    ? emails.map(e => e?.trim()).filter(Boolean)
    : typeof emails === "string"
    ? emails.split(",").map(e => e.trim()).filter(Boolean)
    : [];

  const emailList = cleanedEmails.join(", ");
  const hasEmails = cleanedEmails.length > 0;

  // 🔹 Fonction déclenchée au clic sur le bouton
  const handleCopy = async () => {
    // Si le tableau est vide → on ne fait rien
    if (!hasEmails) {
    setSnackbar({
        open: true,
        message: t("NoValidEmail"),
        severity: "warning"
      });
      return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        // ✅ API moderne (PC, Android récents, iOS >= 15)
        await navigator.clipboard.writeText(emailList);
      } else {
        // ✅ Fallback pour navigateurs plus anciens
        const textarea = document.createElement("textarea");
        textarea.value = emailList;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px"; // hors écran
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      // Message de confirmation (traduit via LanguageContext)
     setSnackbar({
        open: true,
        message: t("EmailsCopied") || "Emails copiés !",
        severity: "success"
      });
    } catch (err) {
      // Gestion des erreurs
      logDev("❌ Erreur copie emails", { message: err.message, stack: err.stack });
      setSnackbar({
        open: true,
        message: t("ErrorCopyingEmails") || "Erreur lors de la copie",
        severity: "error"
      });
    }
  };

  // Si pas d'emails → on n'affiche rien
  if (!emails.length) return null; // Ne rien afficher si pas d'emails

   return (
    <>
    <Button
      variant="contained"
      onClick={handleCopy}
      disabled={disabled}
      startIcon={<ContentCopyIcon />}
      sx={{
        backgroundColor: "#3399FF !important",
        color: "#fff !important",
        marginLeft: 1,
        marginTop: 1,
        width: isMobile ? 10 : 30,
        height: isMobile ? 20 : 60,
        fontWeight: "bold",
        fontSize: isMobile ? "0.5rem" : "0.7rem",
        padding: "10px 20px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
          backgroundColor: "#027bb5",
          transform: "scale(1.05)"
        }
      }}
    >
      {/*{t("CopyEmails")}*/}
    </Button>

    {hasEmails && (
    <Typography variant="caption" sx={{ mt: 1, color: "#555" }}>
      {cleanedEmails.length} {t("EmailsAvailable")}
    </Typography>
  )}

    <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BoutonEmailPeriodeDominante;
